import { useCallback, useState } from 'react';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import {
	APP_TEAM_NAME,
	useExperienceTracker,
} from '@atlassian/jira-project-create-importer-common';
import { APP_IDENTIFIER } from '../../common/constants';
import type { SupportedThirdParty } from '../../common/constants/import-type';
import { EXPERIENCES, thirdPartyAccessTokenStatusMetric } from '../../common/constants/metrics';
import { useGetThirdPartyClient } from '../../common/utils/third-party-clients';
import type { UseThirdPartyAccessStatusToken, UseThirdPartyAccessTokenStatusProps } from './types';

export const useThirdPartyAccessTokenStatus = ({
	requestAuthenticationToken,
}: UseThirdPartyAccessTokenStatusProps = {}): UseThirdPartyAccessStatusToken => {
	const thirdPartyClient = useGetThirdPartyClient({
		requestAuthenticationToken,
	});
	const [onExperienceStart, onExperienceSuccess, onExperienceFail] = useExperienceTracker(
		EXPERIENCES.THIRD_PARTY_POST_ACCESS_TOKEN_STATUS,
	);
	const [isLoading, setIsLoading] = useState(false);
	const [hasErrorOccurred, setHasErrorOccurred] = useState(false);

	const validateAccessToken = useCallback(
		async (thirdParty: SupportedThirdParty) => {
			setIsLoading(true);
			setHasErrorOccurred(false);
			thirdPartyAccessTokenStatusMetric.start();
			onExperienceStart();

			try {
				const newThirdPartyClient = thirdPartyClient(thirdParty);
				const data = await newThirdPartyClient.validateAccessToken();
				thirdPartyAccessTokenStatusMetric.stop();
				onExperienceSuccess();

				setIsLoading(false);
				return data.status;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				thirdPartyAccessTokenStatusMetric.stop();
				onExperienceFail(APP_IDENTIFIER, error, { thirdParty });
				fireErrorAnalytics({
					meta: {
						id: 'post-third-party-access-token-status',
						packageName: APP_IDENTIFIER,
						teamName: APP_TEAM_NAME,
					},
					sendToPrivacyUnsafeSplunk: true,
					error,
				});

				setHasErrorOccurred(true);
				setIsLoading(false);
				return null;
			}
		},
		[onExperienceFail, onExperienceStart, onExperienceSuccess, thirdPartyClient],
	);

	return { isLoading, hasErrorOccurred, validateAccessToken };
};
